import React, { FC, useContext, useState, useEffect, useMemo } from 'react'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import Divider from '@mui/material/Divider'
import Box from '@mui/material/Box'
import ManageContext from '../contexts/manage'

import {
  getTitle as getPriceTitle,
} from '@kaidam/fc-shared/src/utils/price'

import {
  TableSchema,
} from '@kaidam/fc-frontend/src/types/app'

import Table from '@kaidam/fc-frontend/src/components/table/SimpleTable'

import {
  DAY,
  getDateTimeString,
  getDateString,
  getReference,
  getDateTitle,
  isDateTodayOrBefore,
} from '@kaidam/fc-shared/src/booking/utils'

const paymentTableSchema: TableSchema = [{
  title: 'Date',
  name: 'date',
}, {
  title: 'Amount',
  name: 'amount',
}, {
  title: 'Type',
  name: 'type',
}]

const ManageBooking: FC<React.PropsWithChildren<unknown>> = ({}) => {
  const manage = useContext(ManageContext)
  const [ payMore, setPayMore ] = useState<number | ''>(null)

  let payMoreNumber = parseInt('' + payMore)
  if(isNaN(payMoreNumber)) payMoreNumber = 0

  useEffect(() => {
    if(!manage.data) return
    let remaining = manage.data.booking.cost - manage.data.booking.paid
    remaining = remaining < 0 ? 0 : remaining
    setPayMore(Math.floor(remaining / 100))
  }, [
    manage.data,
  ])

  const paymentTableData = useMemo(() => {
    return manage.data.booking.data.payments.map(payment => {
      return {
        date: getDateTitle(payment.date),
        amount: getPriceTitle(payment.amount),
        type: payment.source == 'stripe' ? (
          <div>
            stripe: { payment.reference }
          </div>
        ) : (
          <div>
            manual &nbsp; 
          </div>
        ),
        invoice: payment.pdf ? (
          <div>
            { payment.pdf }
          </div>
        ) : null,
      }
    })
  }, [
    manage.data,
  ])

  useEffect(() => {
    if(!manage.initialised) return
    manage.checkPayment()
  }, [
    manage.initialised,
  ])

  if(!manage.data) return null

  return (
    <Box>
      <Typography
        align="left"
        gutterBottom
        variant="h6"
      >
        Current Status
      </Typography>
      <Typography
        align="left"
        gutterBottom
        variant="body1"
      >
        Below is the current payment status of your booking - you can pay more at any time using the form below...
      </Typography>
      {
        manage.data.booking.cost > 0 ? (
          <>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'row', 
              alignItems: 'center', 
              mt: 2, 
              mb: 2 
            }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={2}>
                  <Typography
                    align="left"
                    gutterBottom
                    variant="body1"
                  >
                    Cost:
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={10}>
                  <Typography
                    align="left"
                    gutterBottom
                    variant="subtitle1"
                  >
                    <strong>{ getPriceTitle(manage.data.booking.cost) }</strong>
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Typography
                    align="left"
                    gutterBottom
                    variant="body1"
                  >
                    Paid:
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={10}>
                  <Typography
                    align="left"
                    gutterBottom
                    variant="subtitle1"
                  >
                    <strong>{ getPriceTitle(manage.data.booking.paid) }</strong>
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Typography
                    align="left"
                    gutterBottom
                    variant="body1"
                  >
                    Remaining:
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={10}>
                  <Typography
                    align="left"
                    gutterBottom
                    variant="h6"
                    color="primary"
                  >
                    <strong>{ getPriceTitle(manage.data.booking.cost - manage.data.booking.paid < 0 ? 0 : manage.data.booking.cost - manage.data.booking.paid) }</strong>
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </>
        ) : (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'row', 
            alignItems: 'center', 
            mt: 2, 
            mb: 2 
          }}>
            <Typography
              align="left"
              gutterBottom
              variant="body1"
              sx={{ mr: 5 }}
            >
              Your booking is
            </Typography>
            <Typography
              align="left"
              gutterBottom
              variant="h4"
              color="primary"
            >
              Free
            </Typography>
          </Box>
        )
      }
      {
        manage.data.booking.cost > 0 && (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'row', 
            alignItems: 'center', 
            mt: 2, 
            mb: 2 
          }}>
            <Typography
              align="left"
              gutterBottom
              variant="h4"
              color="primary"
              sx={{ mr: 2 }}
            >
              &pound;
            </Typography>
            <TextField
              label="Pay Now"
              value={ typeof(payMore) == 'number' ? payMore : '' }
              inputProps={{
                type: 'number',
              }}
              onChange={ (e) => setPayMore(e.target.value ? parseFloat(e.target.value) : '') }
              sx={{ mr: 2 }}
            />
            <Button
              variant="contained"
              color="primary"
              disabled={ payMoreNumber <= 0 }
              onClick={
                () => {
                  if(typeof(payMore) != 'number') return
                  manage.pay(payMore)
                }
              }
            >
              Pay Now
            </Button>
          </Box>
        )
      }
      <Divider sx={{ my: 4 }} />
      <Typography
        align="left"
        gutterBottom
        variant="h6"
      >
        Previous Payments
      </Typography>
      <Typography
        align="left"
        gutterBottom
        variant="body1"
      >
        Here are the previous payments made against this booking.
      </Typography>
      <Table
        data={ paymentTableData }
        fields={ paymentTableSchema }
      />
    </Box>
  )
}

export default ManageBooking